/**
 * Search Brandfetch Tool
 *
 * Searches Brandfetch API for company logos and brand colors.
 * Returns available assets without downloading them.
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { SearchBrandfetchParamsSchema, } from "./searchBrandfetch.schema";
import { BRANDFETCH_CUSTOMER_ID, BRANDFETCH_API_KEY, BRANDFETCH_SEARCH_URL, BRANDFETCH_BRAND_URL, } from "../../config";
// ═══════════════════════════════════════════════════════════════════════════
// BRANDFETCH API CLIENT
// ═══════════════════════════════════════════════════════════════════════════
/** Custom error for rate limiting */
class RateLimitError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RateLimitError';
    }
}
/**
 * Search Brandfetch for a company by name.
 * Returns list of potential matches with domains.
 * @throws {RateLimitError} When API rate limit is hit
 */
async function searchBrandfetchByName(companyName, apiKey) {
    try {
        const response = await fetch(`${BRANDFETCH_SEARCH_URL}/${encodeURIComponent(companyName)}?c=${apiKey}`, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
        });
        if (response.ok) {
            const results = await response.json();
            return Array.isArray(results) ? results : [];
        }
        else if (response.status === 429) {
            // Rate limit hit - throw error to stop further attempts
            console.warn('[SearchBrandfetch] ⚠️ Rate limit hit (429)');
            throw new RateLimitError('Brandfetch API rate limit exceeded. Please try again in a few minutes.');
        }
        else {
            console.warn(`[SearchBrandfetch] Search failed: ${response.status} ${response.statusText}`);
            return [];
        }
    }
    catch (error) {
        // Re-throw rate limit errors
        if (error instanceof RateLimitError) {
            throw error;
        }
        console.error('[SearchBrandfetch] Search error:', error);
        return [];
    }
}
/**
 * Fetch full brand details by domain.
 * Returns logos, colors, and metadata.
 * @throws {RateLimitError} When API rate limit is hit
 */
async function fetchBrandDetails(domain, apiKey) {
    try {
        const response = await fetch(`${BRANDFETCH_BRAND_URL}/${encodeURIComponent(domain)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Accept': 'application/json',
            },
        });
        if (response.ok) {
            return await response.json();
        }
        else if (response.status === 404) {
            // Domain not found - caller may want to search by name instead
            return null;
        }
        else if (response.status === 429) {
            // Rate limit hit - throw error to stop further attempts
            console.warn('[SearchBrandfetch] ⚠️ Rate limit hit (429)');
            throw new RateLimitError('Brandfetch API rate limit exceeded. Please try again in a few minutes.');
        }
        else {
            console.warn(`[SearchBrandfetch] Brand fetch failed: ${response.status} ${response.statusText}`);
            return null;
        }
    }
    catch (error) {
        // Re-throw rate limit errors
        if (error instanceof RateLimitError) {
            throw error;
        }
        console.error('[SearchBrandfetch] Brand fetch error:', error);
        return null;
    }
}
/**
 * Extract available logo variants from brand data.
 * Maps Brandfetch Brand API response to Logo API variant format (theme_type)
 */
function extractLogos(brandData) {
    const bestByVariant = new Map();
    // Format priority: SVG > PNG > WEBP > JPEG/JPG
    const formatPriority = ['svg', 'png', 'webp', 'jpeg', 'jpg'];
    const getFormatRank = (format) => {
        const idx = formatPriority.indexOf(format.toLowerCase());
        return idx === -1 ? formatPriority.length : idx;
    };
    for (const logo of brandData.logos || []) {
        // Extract theme and type from Brand API response
        const logoType = logo.type?.toLowerCase() || '';
        const logoTheme = logo.theme?.toLowerCase() || '';
        // Determine type: icon, logo, or symbol
        let type;
        if (logoType.includes('icon')) {
            type = 'icon';
        }
        else if (logoType.includes('symbol')) {
            type = 'symbol';
        }
        else {
            type = 'logo'; // default
        }
        // Determine theme: light, dark, or none
        let theme = null;
        if (logoTheme === 'light' || logoType.includes('light')) {
            theme = 'light';
        }
        else if (logoTheme === 'dark' || logoType.includes('dark')) {
            theme = 'dark';
        }
        // Build variant string
        const variant = theme ? `${theme}_${type}` : type;
        // Get best format based on priority
        const formats = logo.formats || [];
        let bestFormat = null;
        // Try each format in priority order
        for (const preferredFormat of formatPriority) {
            const found = formats.find(fmt => fmt.format?.toLowerCase() === preferredFormat);
            if (found) {
                bestFormat = found;
                break;
            }
        }
        // If no preferred format found, take the first available format
        if (!bestFormat && formats.length > 0) {
            bestFormat = formats[0];
        }
        if (bestFormat) {
            const formatType = (bestFormat.format?.toLowerCase() || 'png');
            const candidate = {
                variant: variant, // Cast to union type
                format: formatType,
                url: bestFormat.src,
            };
            const existing = bestByVariant.get(variant);
            if (!existing || getFormatRank(candidate.format) < getFormatRank(existing.format)) {
                bestByVariant.set(variant, candidate);
            }
        }
        else {
            // Log warning if logo has no formats (shouldn't happen, but defensive)
            console.warn(`[SearchBrandfetch] Logo variant "${variant}" has no formats, skipping`);
        }
    }
    return Array.from(bestByVariant.values());
}
/**
 * Extract brand colors from brand data.
 */
function extractColors(brandData) {
    const colors = [];
    const colorList = brandData.colors || [];
    // Take up to 5 colors, prioritizing by type
    for (let i = 0; i < Math.min(colorList.length, 5); i++) {
        const color = colorList[i];
        const hex = color.hex || color.value;
        if (hex) {
            // Map Brandfetch color types to our types
            let colorType;
            const type = color.type?.toLowerCase() || '';
            if (type.includes('accent') || i >= 2) {
                colorType = 'accent';
            }
            else if (type.includes('secondary') || i === 1) {
                colorType = 'secondary';
            }
            else {
                colorType = 'primary';
            }
            colors.push({
                hex: hex.startsWith('#') ? hex : `#${hex}`,
                type: colorType,
            });
        }
    }
    return colors;
}
/**
 * Check if a string looks like a domain (contains a dot).
 */
function looksLikeDomain(query) {
    return query.includes('.') && !query.includes(' ');
}
/**
 * Extract a searchable name from a domain.
 */
function domainToSearchName(domain) {
    return domain
        .replace(/\.(com|io|net|org|co|ai|dev|tech|app)$/i, '')
        .replace(/[-_]/g, ' ');
}
// ═══════════════════════════════════════════════════════════════════════════
// SEARCH BRANDFETCH TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const SEARCH_BRANDFETCH_SYSTEM_PROMPT = `### searchBrandfetch
Search for company logos and brand colors via Brandfetch.

**When to use:**
- Finding official company logos
- Getting brand colors for consistent styling
- Looking up logo variants (primary, white, dark)

**Search tip:** Use company name, not domain.

**This tool only searches** - it does NOT download assets.
After search, call retrieveBrandfetch to download specific logos.

\`\`\`json
{
  "companyName": "Microsoft"
}
\`\`\`

Returns: domains, available logo types, and color palettes.`;
/**
 * Tool for searching Brandfetch for logos and brand colors.
 *
 * Does NOT download assets - use retrieveBrandfetch for that.
 */
export class SearchBrandfetchTool extends ToolBase {
    constructor() {
        super();
        this.name = "searchBrandfetch";
        // System prompt description
        this.systemPromptDescription = SEARCH_BRANDFETCH_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'query', type: 'string', list_status: false, order: 1 },
            { name: 'assetTypes', type: 'array', list_status: true, order: 2 },
        ];
        this.listsToCheck = [
            {
                name: 'assetTypes',
                type: 'array',
                item_type: 'string',
                list_status: true,
                order: 2,
            },
        ];
        this.description = new ToolDescription("searchBrandfetch", `Search Brandfetch for company logos and brand colors.

**Purpose:** Find available brand assets for a company without downloading them.

**Parameters:**
- query: Company name or domain (e.g., "Microsoft" or "microsoft.com")
- assetTypes: What to search for - ["logo"], ["brand_colors"], or both

**Example:**
\`\`\`json
{
  "query": "Microsoft",
  "assetTypes": ["logo", "brand_colors"]
}
\`\`\`

**Returns:**
- companyDomain: Resolved domain (e.g., "microsoft.com")
- companyName: Company name
- availableLogos: Array of logo variants (primary, white, dark, icon)
- availableColors: Array of brand colors (primary, secondary, accent)

**Note:** Use retrieveBrandfetch to download specific assets after searching.`, SearchBrandfetchParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(SearchBrandfetchParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        console.log(`[SearchBrandfetchTool] Searching for "${params.query}" (types: ${params.assetTypes.join(", ")})`);
        if (!BRANDFETCH_API_KEY) {
            return {
                success: false,
                companyDomain: '',
                companyName: params.query,
                availableLogos: [],
                availableColors: [],
                error: 'Brandfetch API key not configured. Set BRANDFETCH_API_KEY in config.ts',
            };
        }
        const apiKey = BRANDFETCH_API_KEY;
        try {
            let brandData = null;
            let resolvedDomain = '';
            let resolvedName = params.query;
            // Strategy 1: If query looks like a domain, try direct lookup first
            if (looksLikeDomain(params.query)) {
                console.log(`[SearchBrandfetchTool] Query looks like domain, trying direct lookup...`);
                brandData = await fetchBrandDetails(params.query, apiKey);
                if (brandData) {
                    resolvedDomain = brandData.domain || params.query;
                    resolvedName = brandData.name || domainToSearchName(params.query);
                }
            }
            // Strategy 2: If no result yet, search by name
            if (!brandData) {
                const searchName = looksLikeDomain(params.query)
                    ? domainToSearchName(params.query)
                    : params.query;
                console.log(`[SearchBrandfetchTool] Searching by name: "${searchName}"`);
                const searchResults = await searchBrandfetchByName(searchName, BRANDFETCH_CUSTOMER_ID);
                if (searchResults.length > 0) {
                    const bestMatch = searchResults[0];
                    resolvedDomain = bestMatch.domain;
                    resolvedName = bestMatch.name;
                    console.log(`[SearchBrandfetchTool] Found match: ${resolvedName} (${resolvedDomain})`);
                    // Fetch full brand details for the matched domain
                    brandData = await fetchBrandDetails(resolvedDomain, apiKey);
                }
            }
            // Build response
            if (!brandData) {
                console.log(`[SearchBrandfetchTool] No brand found for "${params.query}"`);
                return {
                    success: false,
                    companyDomain: '',
                    companyName: params.query,
                    availableLogos: [],
                    availableColors: [],
                    error: `No brand found for "${params.query}". Try a different company name or domain.`,
                };
            }
            // Extract logos and colors based on requested asset types
            const availableLogos = params.assetTypes.includes('logo')
                ? extractLogos(brandData)
                : [];
            const availableColors = params.assetTypes.includes('brand_colors')
                ? extractColors(brandData)
                : [];
            const result = {
                success: true,
                companyDomain: resolvedDomain,
                companyName: resolvedName,
                availableLogos,
                availableColors,
            };
            console.log(`[SearchBrandfetchTool] ✅ Found ${result.availableLogos.length} logos, ${result.availableColors.length} colors for ${resolvedName}`);
            return result;
        }
        catch (error) {
            // Handle rate limit errors with specific messaging
            if (error instanceof RateLimitError) {
                console.error("[SearchBrandfetchTool] ⚠️ Rate limit:", error.message);
                return {
                    success: false,
                    companyDomain: '',
                    companyName: params.query,
                    availableLogos: [],
                    availableColors: [],
                    error: `⚠️ Rate limit exceeded: ${error.message}`,
                };
            }
            console.error("[SearchBrandfetchTool] ❌ Error:", error);
            return {
                success: false,
                companyDomain: '',
                companyName: params.query,
                availableLogos: [],
                availableColors: [],
                error: `Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
        }
    }
}
